<template>
  <div>
    <right-panel class="float-right" id="sidebar-import-inventory" width="800px" :icon="'mdi mdi-plus'" :text="$t('inventory-import.import')" @hidden="rightPanelClosed">
      <div class="pl-5 pr-5">
        <Form :hide-library="true" />
      </div>
    </right-panel>

    <h4 v-if="selectedOrganization">
      <span>{{ selectedOrganization.name }} - {{ $t("inventory-import.import") }} </span>
    </h4>

    <div class="alert alert-info d-flex justify-content-between align-items-center" role="alert">
      <div>
        <p class="mb-1">
          {{ $t("inventory-import.template.instructions") }}
          <br />
          <small>{{ $t("inventory-import.template.off-hours-warning") }}</small>
        </p>
        <small v-if="inventoryTemplateUpdatedAt" class="text-muted">
          {{ $t("inventory-import.template.last-updated", { date: $options.filters.date(inventoryTemplateUpdatedAt, true) }) }}
        </small>
      </div>
      <div>
        <sweet-button v-if="inventoryTemplate && inventoryTemplate.data" variant="info" size="xs" icon="mdi mdi-download" @click="downloadInventoryTemplate">
          {{ $t("inventory-import.template.download") }}
        </sweet-button>
        <span v-else class="text-danger">{{ $t("inventory-import.template.missing") }}</span>
      </div>
    </div>

    <div class="m-4">
      <filter-select
        id="jobFilter"
        ref="jobFilter"
        :placeholder="$t('inventory-import.job.text')"
        :multiple="false"
        @filter="onJobSearch"
        @changed="onJobSelect"
        :selected="selectedJob"
      />
    </div>

    <hr />
    <div v-if="selectedJob" class="row mb-4">
      <div class="col-md-3">
        <strong>{{ $t("inventory-import.job.createdby") }}</strong
        >:
        <span v-if="selectedJob.account">{{ selectedJob.account.name }}</span>
      </div>
      <div class="col-md-3">
        <strong>{{ $t("inventory-import.job.started-at") }}</strong
        >:
        <span>{{ selectedJob.startedDate | date(true) }}</span>
      </div>
      <div class="col-md-3">
        <strong>{{ $t("inventory-import.job.completed-at") }}</strong
        >:
        <span>{{ selectedJob.completedDate | date(true) }}</span>
      </div>
      <div class="col-md-3 d-flex align-items-center">
        <div>
          <strong>{{ $t("inventory-import.job.status.text") }}</strong> :

          <span :class="['badge', jobStatusClass]"> {{ jobStatusLabel }} </span>
        </div>
        <sweet-button v-if="canCancelJob" :loading="isCancellingSelectedJob" variant="danger" size="xs" class="ml-2" icon="mdi mdi-stop" @click="cancelSelectedJob">
          {{ $t("inventory-import.job.cancel") }}
        </sweet-button>
      </div>
    </div>
    <hr />

    <!-- Virtual scroll listesi -->
    <div v-if="selectedJob" class="position-relative">
      <!-- Tablo başlığı -->
      <div class="bg-light-secondary">
        <div class="row">
          <div class="col-md-3 text-center">
            <strong>{{ $t("inventory-import.job-item.step") }}</strong>
          </div>
          <div class="col-md-3 text-center">
            <strong>{{ $t("inventory-import.job-item.status") }}</strong>
          </div>
          <div class="col-md-3 text-center">
            <strong>{{ $t("inventory-import.job-item.execution-time") }}</strong>
          </div>
          <div class="col-md-3 text-center">
            <strong>{{ $t("inventory-import.job-item.detail") }}</strong>
          </div>
        </div>
      </div>

      <!-- Sadece görünen satırlar DOM'a basılır -->
      <RecycleScroller
        ref="scroller"
        :items="jobItems"
        :item-size="40"
        key-field="_id"
        style="height: 520px; overflow-y: auto;"
        @scroll.native="onScroll"
      >
        <template v-slot="{ item }">
          <div class="row border-bottom" style="height: 40px; align-items: center;">
            <div class="col-md-3 border-right text-truncate" :title="jobItemStepLabel(item.step)">
              <small>{{ jobItemStepLabel(item.step) }}</small>
            </div>
            <div class="col-md-3 border-right">
              <span v-if="item.status == 'SUCCESS'"><i class="fa fa-check text-success"></i></span>
              <span v-else-if="item.status == 'FAILED'"><i class="fa fa-times text-danger"></i></span>
              <span v-else-if="item.status == 'PROCESSING'"><i class="bx bx-hourglass bx-spin font-size-16"></i></span>
              <span v-else-if="item.status == 'CANCELLED'"><i class="mdi mdi-stop-circle text-warning"></i></span>
              <small>{{ jobItemStatusLabel(item.status) }}</small>
            </div>
            <div class="col-md-3 border-right">
              <small>{{ item.executionTime }}</small>
            </div>
            <div class="col-md-3 border-right text-truncate" :title="jobItemMessageLabel(item.message)">
              <small>{{ jobItemMessageLabel(item.message) }}</small>
            </div>
          </div>
        </template>
      </RecycleScroller>

      <!-- Kullanıcı yukarı scroll etmişse "En Yeniye Git" butonu -->
      <button
        v-if="showScrollToLatest"
        class="btn btn-sm btn-primary shadow"
        style="position: absolute; bottom: 12px; right: 16px; z-index: 10;"
        @click="scrollToLatest"
      >
        <i class="mdi mdi-arrow-down"></i> {{ $t("inventory-import.job-item.scroll-to-latest") }}
      </button>
    </div>

    <hr />
  </div>
</template>

<script>
import { EventBus } from "@modules/websocket";
import Form from "./management-inventory-import-form";
import { RecycleScroller } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";

const state = "managementInventoryImport";
const SCROLL_BOTTOM_THRESHOLD = 60; // px — bu kadar yakınsa "en altta" sayılır

export default {
  components: { Form, RecycleScroller },
  data() {
    return {
      isAtBottom: true,
      showScrollToLatest: false,
    };
  },

  computed: {
    jobItems() {
      return this.$store.getters[`${state}/jobItems`];
    },
    jobItemCount() {
      return this.jobItems.length;
    },
    selectedJob() {
      return this.$store.getters[`${state}/selectedJob`];
    },
    jobStatusLabel() {
      const status = this.selectedJob && this.selectedJob.status;
      if (!status) return "";
      return this.$t(`inventory-import.job.status.${status}`);
    },
    jobStatusClass() {
      const status = this.selectedJob && this.selectedJob.status;
      switch (status) {
        case "PROCESSING":
          return "badge-main";
        case "COMPLETED":
          return "badge-success";
        case "FAILED":
          return "badge-danger";
        case "CANCEL_REQUESTED":
          return "badge-warning";
        case "CANCELLED":
          return "badge-dark";
        default:
          return "badge-secondary";
      }
    },
    canCancelJob() {
      const status = this.selectedJob && this.selectedJob.status;
      return status === "PROCESSING" || status === "CANCEL_REQUESTED";
    },
    isCancellingSelectedJob() {
      const id = this.selectedJob && this.selectedJob._id;
      if (!id) return false;
      return this.$store.getters[`${state}/isCancelling`](id);
    },
    inventoryTemplate() {
      const config = this.$store.getters["managementConfig/config"] || {};
      return config.inventoryTemplate;
    },
    inventoryTemplateUpdatedAt() {
      return this.inventoryTemplate && this.inventoryTemplate.updatedAt;
    },
    selectedOrganization: {
      get() {
        return this.$store.getters[`${state}/selectedOrganization`];
      },
      set(v) {
        this.$store.commit(`${state}/selectedOrganization`, v);
      },
    },
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch("managementConfig/list");
    } catch (error) {}
    if (this.$route.params.id) {
      await this.findOrganizationById(this.$route.params.id);
    }

    window.addEventListener("beforeunload", this.beforeUnload);
    EventBus.$on("inventory-import-progress", (e) => {
      if (!this._wsBatch) this._wsBatch = [];
      if (Array.isArray(e.jobItems)) {
        this._wsBatch.push(...e.jobItems);
      } else {
        this._wsBatch.push(e);
      }
      if (!this._wsRafPending) {
        this._wsRafPending = true;
        requestAnimationFrame(() => {
          this.$store.commit(`${state}/addJobItemBatch`, this._wsBatch.splice(0));
          this._wsRafPending = false;
        });
      }
    });
    EventBus.$on("inventory-import-job-completed", (e) => {
      this.$store.commit(`${state}/selectedJob`, e.job);
    });
  },
  beforeRouteLeave(to, from, next) {
    const answer = window.confirm("Do you really want to leave? you have unsaved changes!");
    if (answer) {
      next();
    } else {
      next(false);
    }
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },

  watch: {
    selectedJob: {
      deep: true,
      immediate: true,
      handler(v) {
        this.fetchJobItems();
      },
    },
    jobItemCount(newLen, oldLen) {
      if (newLen > oldLen && this.isAtBottom) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
  },
  methods: {
    scrollToBottom() {
      const el = this.$refs.scroller?.$el;
      if (el) el.scrollTop = el.scrollHeight;
    },
    onScroll(e) {
      const el = e.target;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.isAtBottom = distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;
      this.showScrollToLatest = !this.isAtBottom;
    },
    scrollToLatest() {
      this.scrollToBottom();
      this.isAtBottom = true;
      this.showScrollToLatest = false;
    },
    async fetchJobItems() {
      this.$store.commit(`${state}/jobItems`, []);

      if (!this.selectedJob) {
        return;
      }

      const organizationId = (this.selectedOrganization && this.selectedOrganization._id) || this.$route.params.id;
      await this.$store.dispatch(`${state}/fetchJobItems`, {
        organizationId,
        jobId: this.selectedJob._id,
      });
      this.$nextTick(() => this.scrollToBottom());
    },
    downloadInventoryTemplate() {
      if (!this.inventoryTemplate || !this.inventoryTemplate.data) {
        return;
      }
      const link = document.createElement("a");
      link.href = this.inventoryTemplate.data;
      link.download = this.inventoryTemplate.fileName || "inventory-template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async cancelSelectedJob() {
      if (!(this.selectedJob && this.selectedJob._id)) {
        return;
      }

      const ok = await this.confirm(this.$t("inventory-import.job.cancel-confirm"));
      if (!ok) {
        return;
      }

      try {
        await this.$store.dispatch(`${state}/cancelJob`, { jobId: this.selectedJob._id });
        this.$toast.info(this.$t("inventory-import.job.cancel-requested"));
      } catch (error) {
        const message = (error && error.data && error.data.message) || this.$t("common.failed");
        this.$toast.error(message);
      }
    },
    jobItemStatusLabel(status) {
      if (this.$te(`inventory-import.job-item.statuses.${status}`)) {
        return this.$t(`inventory-import.job-item.statuses.${status}`);
      }
      return status;
    },
    jobItemStepLabel(step) {
      const map = {
        "Cancel inventory import requested": "inventory-import.job-item.steps.cancel-requested",
        "Cancel inventory import": "inventory-import.job-item.steps.cancelled",
      };

      const key = map[step];
      if (key && this.$te(key)) {
        return this.$t(key);
      }
      return step;
    },
    jobItemMessageLabel(message) {
      if (!message) {
        return "";
      }

      const messageMap = {
        "Waiting for current operations to finish": "inventory-import.job-item.messages.waiting-current",
        "Inventory import cancelled by user": "inventory-import.job-item.messages.cancelled",
      };

      const directKey = messageMap[message];
      if (directKey && this.$te(directKey)) {
        return this.$t(directKey);
      }

      const match = message.match(/Inventory import cancelled by user before row\s*(\d+)/i);
      if (match) {
        const row = match[1];
        const key = "inventory-import.job-item.messages.cancelled-before-row";
        if (this.$te(key)) {
          return this.$t(key, { row });
        }
      }

      return message;
    },
    async findOrganizationById(id) {
      await this.$store.dispatch(`${state}/fetchOrganizationDetail`, id);
    },
    rightPanelClosed() {},
    beforeUnload(e) {
      if (this.editedStages.length == 0) {
        return undefined;
      }

      (e || window.event).returnValue = this.reloadMessage;
      return this.reloadMessage;
    },

    loadStages() {
      return this.$store.dispatch(`${state}/loadStages`);
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`${state}/save`);
        this.loadStages();
        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        console.error(error);
        const message = (error && error.data && error.data.message) || undefined;
        if (message) {
          this.$toast.error(message);
        }
      }
    },
    async onJobSelect(item) {
      if (item) {
        this.$store.commit(`${state}/selectedJob`, item);
      } else {
        this.$store.commit(`${state}/selectedJob`, undefined);
      }

      await this.fetchJobItems();
    },

    async onJobSearch(value, callback) {
      const response = await this.$store.dispatch(`${state}/paginateJobs`, {
        organizationId: (this.selectedOrganization && this.selectedOrganization._id) || this.$route.params.id,
      });

      const docs = (response.data && response.data.docs) || [];
      const options = docs.map((i) => ({ ...i, name: this.$options.filters.date(i.name, true) }));

      callback(options);
    },
    removeTaskItem(task) {
      this.$store.commit(`${state}/removeTaskItem`, { task });
    },
    editTaskItem(task) {
      this.$store.commit(`${state}/editedTask`, { ...task });

      this.$root.$emit("bv::toggle::collapse", `sidebar-create-task-` + task.stage);
    },
  },
};
</script>
